/**
 * useBrowserNotifications — OS-level alerts for events that need a human.
 *
 * Fires a Notification when a HITL request, governance alert, or governance
 * escalation arrives while the tab is hidden. Without this, HITL timeout
 * countdowns are invisible to anyone who switched tabs — the request just
 * expires.
 *
 * Permission is requested once on mount (post-auth); browsers that gate
 * requestPermission on a user gesture simply leave permission at 'default'
 * until a later mount, which is fine — this is best-effort.
 */

import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useExecutionStore } from './useExecutionStore'
import { useEscalationStore } from './useEscalations'

function canNotify(): boolean {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    Notification.permission === 'granted' &&
    document.visibilityState === 'hidden'
  )
}

function showNotification(title: string, body: string, tag: string): void {
  try {
    const notification = new Notification(title, { body, tag })
    notification.onclick = () => {
      window.focus()
      notification.close()
    }
  } catch (err) {
    // Notification constructor can throw on some platforms (e.g. Android
    // Chrome requires a ServiceWorkerRegistration) — never break the app.
    console.warn('[useBrowserNotifications] Failed to show notification:', err)
  }
}

export function useBrowserNotifications(): void {
  const { t } = useTranslation('chat')
  // Stable ref to t — subscriptions live outside the React render cycle
  const tRef = useRef(t)
  tRef.current = t

  // One-time permission request (no-op when already granted/denied)
  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return
    if (Notification.permission === 'default') {
      void Notification.requestPermission()
    }
  }, [])

  // New HITL requests
  useEffect(() => {
    return useExecutionStore.subscribe(
      (s) => s.hitlRequests,
      (curr, prev) => {
        if (!canNotify()) return
        for (const [id, req] of curr) {
          if (!prev.has(id)) {
            showNotification(
              tRef.current('notifications.hitlTitle', 'Mission needs your response'),
              `${req.missionName}: ${req.prompt}`.slice(0, 180),
              `hitl-${id}`,
            )
          }
        }
      },
    )
  }, [])

  // New governance alerts (live WebSocket deny/escalate)
  useEffect(() => {
    return useExecutionStore.subscribe(
      (s) => s.governanceAlerts,
      (curr, prev) => {
        if (!canNotify()) return
        for (const [id, alert] of curr) {
          if (!prev.has(id)) {
            showNotification(
              tRef.current('notifications.governanceTitle', 'Governance alert'),
              `${alert.missionName}: ${alert.reason ?? alert.action}`.slice(0, 180),
              `gov-${id}`,
            )
          }
        }
      },
    )
  }, [])

  // New pending escalations (REST-backed inbox)
  useEffect(() => {
    return useEscalationStore.subscribe(
      (s) => s.escalations,
      (curr, prev) => {
        if (!canNotify()) return
        const prevIds = new Set(prev.map((e) => e.id))
        for (const esc of curr) {
          if (esc.status === 'pending' && !prevIds.has(esc.id)) {
            showNotification(
              tRef.current('notifications.escalationTitle', 'Escalation awaiting decision'),
              esc.reason.slice(0, 180),
              `esc-${esc.id}`,
            )
          }
        }
      },
    )
  }, [])
}
